import React from "react";
import Page from "../UI/Page";
import {
  Box,
  Container,
  Heading,
  Text,
  Divider,
  VStack,
  Card,
  CardBody,
  HStack,
  Icon,
  Badge,
  SimpleGrid,
} from "@chakra-ui/react";
import { StarIcon, TimeIcon } from "@chakra-ui/icons";

// ─── Reusable card for the 3-column sections ────────────────
const InfoCard = ({ emoji, title, description, badge, borderColor, hoverColor }) => (
  <Card
    bg="rgba(45, 55, 72, 0.7)"
    borderWidth="1px"
    borderColor={`${borderColor}.700`}
    backdropFilter="blur(10px)"
    _hover={{
      transform: "translateY(-4px)",
      boxShadow: `0 0 25px ${hoverColor}`,
      borderColor: `${borderColor}.500`,
    }}
    transition="all 0.3s"
    height="100%"
  >
    <CardBody p={{ base: 3, md: 5 }}>
      <VStack spacing={{ base: 2, md: 3 }} align="stretch">
        <Text fontSize={{ base: "2xl", md: "3xl" }} textAlign="center">
          {emoji}
        </Text>
        <Heading size={{ base: "sm", md: "md" }} color="white" textAlign="center">
          {title}
        </Heading>
        <Divider borderColor="gray.700" />
        <Text fontSize={{ base: "xs", md: "sm" }} color="gray.300" lineHeight="tall">
          {description}
        </Text>
        <Badge colorScheme={borderColor} textAlign="center" py={1} fontSize="xs">
          {badge}
        </Badge>
      </VStack>
    </CardBody>
  </Card>
);

// ─── Value pill for the bottom grid ──────────────────────────
const ValueCard = ({ emoji, title, subtitle }) => (
  <Card bg="rgba(45, 55, 72, 0.6)" borderWidth="1px" borderColor="gray.700">
    <CardBody p={{ base: 2.5, md: 4 }} textAlign="center">
      <Text fontSize={{ base: "xl", md: "2xl" }} mb={{ base: 1, md: 2 }}>
        {emoji}
      </Text>
      <Text fontSize={{ base: "xs", md: "sm" }} color="teal.200" fontWeight="bold">
        {title}
      </Text>
      <Text
        fontSize={{ base: "2xs", md: "xs" }}
        color="gray.400"
        mt={0.5}
        display={{ base: "none", sm: "block" }}
      >
        {subtitle}
      </Text>
    </CardBody>
  </Card>
);

// ─── Main Component ──────────────────────────────────────────
const About = () => {
  return (
    <Page>
      <div className="hero" style={{ minHeight: "70vh" }}>
        <Box py={{ base: 6, md: 12 }}>
          <Container maxW="900px" px={{ base: 3, md: 4 }}>
            <VStack spacing={{ base: 5, md: 8 }} align="stretch">
              {/* Header */}
              <VStack spacing={{ base: 1, md: 3 }} textAlign="center">
                <Heading
                  size={{ base: "md", md: "xl" }}
                  color="teal.300"
                  fontWeight="extrabold"
                >
                  A Journey of Love, Legacy & Purpose
                </Heading>
                <Text fontSize={{ base: "xs", md: "sm" }} color="gray.400" maxW="700px">
                  Every great story starts with a moment
                </Text>
              </VStack>

              {/* Main Story Card */}
              <Card
                bg="rgba(45, 55, 72, 0.7)"
                borderWidth="2px"
                borderColor="teal.700"
                backdropFilter="blur(10px)"
                boxShadow="0 0 30px rgba(56, 178, 172, 0.2)"
              >
                <CardBody p={{ base: 4, md: 6 }}>
                  <VStack spacing={{ base: 3, md: 4 }} align="stretch">
                    <HStack spacing={2} justify="center">
                      <Text fontSize={{ base: "xl", md: "2xl" }}>❤️</Text>
                      <Text
                        fontSize={{ base: "md", md: "lg" }}
                        color="white"
                        fontWeight="semibold"
                      >
                        The Beginning
                      </Text>
                      <Text fontSize={{ base: "xl", md: "2xl" }}>❤️</Text>
                    </HStack>

                    <Text
                      fontSize={{ base: "sm", md: "md" }}
                      color="gray.300"
                      lineHeight="tall"
                      textAlign="center"
                    >
                      For me, that moment was the day I held my son for the
                      first time –{" "}
                      <Badge
                        colorScheme="blue"
                        fontSize={{ base: "sm", md: "md" }}
                        px={3}
                        py={1}
                        borderRadius="full"
                      >
                        January 10, 2025
                      </Badge>
                      . In that instant, everything changed. This project was
                      born from that overwhelming feeling of love and
                      responsibility.
                    </Text>
                  </VStack>
                </CardBody>
              </Card>

              {/* Three Column Cards */}
              <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={{ base: 3, md: 4 }}>
                <InfoCard
                  emoji="👶"
                  title="Meet Mattias"
                  description="Mattias is the heartbeat of this journey. From his very first breath, he became the reason I strive to build something meaningful—a foundation for his education, passions, and dreams."
                  badge="Born 2025"
                  borderColor="purple"
                  hoverColor="rgba(159, 122, 234, 0.3)"
                />
                <InfoCard
                  emoji="👨‍💻"
                  title="About Me"
                  description="I'm Angelin – a father, a tech enthusiast, and someone who believes in the power of smart investing and long-term thinking. This is my personal promise to prepare him for the future."
                  badge="Dad & Builder"
                  borderColor="cyan"
                  hoverColor="rgba(6, 182, 212, 0.3)"
                />
                <InfoCard
                  emoji="📈"
                  title="The Project"
                  description="This isn't just a tracker—it's a living story of financial wisdom, milestones, and dreams. Built to show how small, consistent actions can grow into something meaningful."
                  badge="Legacy Builder"
                  borderColor="green"
                  hoverColor="rgba(72, 187, 120, 0.3)"
                />
              </SimpleGrid>

              {/* Mission Statement */}
              <Card
                bg="rgba(45, 55, 72, 0.7)"
                borderWidth="2px"
                borderColor="yellow.700"
                backdropFilter="blur(10px)"
                boxShadow="0 0 30px rgba(251, 191, 36, 0.2)"
              >
                <CardBody p={{ base: 4, md: 6 }}>
                  <VStack spacing={{ base: 3, md: 4 }}>
                    <HStack spacing={2}>
                      <Icon as={StarIcon} color="yellow.300" boxSize={{ base: 4, md: 5 }} />
                      <Heading size={{ base: "sm", md: "md" }} color="white">
                        The Mission
                      </Heading>
                      <Icon as={StarIcon} color="yellow.300" boxSize={{ base: 4, md: 5 }} />
                    </HStack>
                    <Text
                      fontSize={{ base: "sm", md: "md" }}
                      color="gray.300"
                      lineHeight="tall"
                      textAlign="center"
                      maxW="700px"
                    >
                      By sharing this project publicly, I hope to inspire other
                      parents and dreamers to start their own journey—to build
                      not just wealth, but a lasting legacy. Every small
                      investment, milestone, or lesson learned matters.
                    </Text>
                  </VStack>
                </CardBody>
              </Card>

              {/* Values — always 3 columns, they're small enough */}
              <SimpleGrid columns={3} spacing={{ base: 2, md: 4 }}>
                <ValueCard
                  emoji="💡"
                  title="Transparency"
                  subtitle="Open sharing of the journey"
                />
                <ValueCard
                  emoji="🎯"
                  title="Long-term"
                  subtitle="Building for the future"
                />
                <ValueCard
                  emoji="❤️"
                  title="Love & Purpose"
                  subtitle="Driven by family values"
                />
              </SimpleGrid>

              {/* Final Message */}
              <Card
                bg="rgba(45, 55, 72, 0.7)"
                borderWidth="2px"
                borderColor="teal.700"
                backdropFilter="blur(10px)"
              >
                <CardBody p={{ base: 4, md: 6 }} textAlign="center">
                  <VStack spacing={{ base: 2, md: 3 }}>
                    <Icon as={TimeIcon} color="teal.300" boxSize={{ base: 5, md: 6 }} />
                    <Heading size={{ base: "sm", md: "md" }} color="white">
                      Because Every Step Counts...
                    </Heading>
                    <Text
                      fontSize={{ base: "xs", md: "sm" }}
                      color="gray.300"
                      lineHeight="tall"
                      maxW="600px"
                    >
                      Whether it's a small investment, a big milestone, or
                      simply a lesson learned—every moment matters. Let's build a
                      future where our children have the freedom to chase their
                      passions without limits.
                    </Text>
                    <Badge
                      colorScheme="teal"
                      fontSize={{ base: "xs", md: "sm" }}
                      px={{ base: 3, md: 4 }}
                      py={{ base: 1, md: 2 }}
                      mt={2}
                    >
                      Started February 2025 🚀
                    </Badge>
                  </VStack>
                </CardBody>
              </Card>
            </VStack>
          </Container>
        </Box>
      </div>
    </Page>
  );
};

export default About;